import { makeStyles } from "tss-react/mui";
import i18next from "i18next";

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ─── Brand tokens ─────────────────────────────────────────────────────────────
export const BRAND = {
  primary:    "#003A22",
  secondary:  "#54CC54",
  tertiary:   "#CEF4C3",
  background: "#F6F9F3",
  textMain:   "#1D2939",
  textMuted:  "#475467",
  border:     "#E5E5E5",
  white:      "#ffffff",
};

// ─── Design tokens ────────────────────────────────────────────────────────────
export const RADIUS = {
  lg: "16px",   // cards, sections, dialogs
  md: "10px",   // buttons, icon wraps, inputs
  sm: "6px",    // tags, pills, small elements
};

export const SHADOW = {
  soft:   "0 1px 4px rgba(0,0,0,0.04)",
  medium: "0 4px 16px rgba(0,0,0,0.08)",
  strong: "0 8px 32px rgba(0,0,0,0.12)",
};

export const FONT = {
  family: "Inter, sans-serif",
  h1:     { fontSize: "clamp(1.4rem, 0.9rem + 1.5vw, 1.75rem)", fontWeight: 700, lineHeight: 1.2 },
  h2:     { fontSize: "1.1rem", fontWeight: 700, lineHeight: 1.3 },
  h3:     { fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.3 },
  body:   { fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5 },
  small:  { fontSize: "0.8rem", fontWeight: 500, lineHeight: 1.4 },
  meta:   { fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.3 },
  tiny:   { fontSize: "0.68rem", fontWeight: 600, lineHeight: 1.3 },
  label:  { fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" },
};

export const TEXTURE_URL = "https://www.aprovall.com/wp-content/uploads/2024/11/texture.webp";

// ─── Breakpoints ──────────────────────────────────────────────────────────────
// Mobile-first responsive helpers. Import `MEDIA` in component styles to keep
// breakpoints consistent across the Light mode.
export const BREAKPOINTS = {
  sm: 600,   // mobile / tablet portrait boundary
  md: 900,   // tablet / small desktop boundary
  lg: 1200,  // desktop / large desktop boundary
};
export const MEDIA = {
  mobile:  `@media (max-width: ${BREAKPOINTS.sm - 1}px)`,                                // < 600
  tablet:  `@media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`, // 600–899
  smDown:  `@media (max-width: ${BREAKPOINTS.md - 1}px)`,                                // < 900 (mobile + tablet)
  mdDown:  `@media (max-width: ${BREAKPOINTS.lg - 1}px)`,                                // < 1200
  mdUp:    `@media (min-width: ${BREAKPOINTS.md}px)`,                                    // ≥ 900
  lgUp:    `@media (min-width: ${BREAKPOINTS.lg}px)`,                                    // ≥ 1200
};

// ─── Statuts dossier ──────────────────────────────────────────────────────────
const _ds = (key, bg, color) => ({ get label() { return i18next.t(`light.status.${key}`); }, bg, color });
export const DOSSIER_STATUS = {
  IN_PROGRESS:       _ds("IN_PROGRESS",       "#EFF6FF", "#1D4ED8"),
  ACTION_REQUIRED:   _ds("ACTION_REQUIRED",   "#FFF7ED", "#EA580C"),
  NOT_VALID:         _ds("NOT_VALID",         "#FEF2F2", "#B91C1C"),
  VALID:             _ds("VALID",             "#F0FDF4", "#15803D"),
  MISSING:           _ds("MISSING",           "#F9FAFB", "#6B7280"),
  BELOW_EXPECTATION: _ds("BELOW_EXPECTATION", "#F3E8FF", "#6D00CC"),
  INCONNU:           _ds("INCONNU",           "#F9FAFB", "#6B7280"),
};

// ─── Statuts document ─────────────────────────────────────────────────────────
const _dd = (key, bg, color) => ({ get label() { return i18next.t(`light.docStatus.${key}`); }, bg, color });
export const DOC_STATUS = {
  VALIDE:            _dd("VALIDE",            "#F0FDF4", "#15803D"),
  INVALIDE:          _dd("INVALIDE",          "#FEF2F2", "#B91C1C"),
  CTRL_EN_COURS:     _dd("CTRL_EN_COURS",     "#EFF6FF", "#1D4ED8"),
  ACTION_DO_REQUISE: _dd("ACTION_DO_REQUISE", "#FFF7ED", "#C2410C"),
  ARRET_DEMANDE:     _dd("ARRET_DEMANDE",     "#F3F4F6", "#6B7280"),
  VALIDATED:         _dd("VALIDATED",         "#F0FDF4", "#15803D"),
  VALID:             _dd("VALID",             "#F0FDF4", "#15803D"),
  NOT_VALID:         _dd("NOT_VALID",         "#FEF2F2", "#B91C1C"),
  ACTION_REQUIRED:   _dd("ACTION_REQUIRED",   "#FFF7ED", "#C2410C"),
  MISSING:           _dd("MISSING",           "#F9FAFB", "#6B7280"),
  BELOW_EXPECTATION: _dd("BELOW_EXPECTATION", "#F3E8FF", "#6D00CC"),
  INCONNU:           _dd("INCONNU",           "#F9FAFB", "#6B7280"),
};

// ─── Statuts contact ──────────────────────────────────────────────────────────
const _dc = (key, bg, color) => ({ get label() { return i18next.t(`light.contactStatus.${key}`); }, bg, color });
export const CONTACT_STATUS = {
  INSCRIT:            _dc("INSCRIT",            "#F0FDF4", "#15803D"),
  INVITE:             _dc("INVITE",             "#EFF6FF", "#1D4ED8"),
  DECLINED:           _dc("DECLINED",           "#FEF2F2", "#B91C1C"),
  EXPIRED_INVITATION: _dc("EXPIRED_INVITATION", "#F9FAFB", "#6B7280"),
  "SANS CONTACT":     _dc("SANS CONTACT",       "#F9FAFB", "#6B7280"),
};

// ─── Shared styles ────────────────────────────────────────────────────────────
export const useLightStyles = makeStyles()(() => ({

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════

  pageRoot: {
    width: "100%", boxSizing: "border-box", flex: 1,
    display: "flex", flexDirection: "column", alignItems: "center",
    fontFamily: FONT.family,
  },
  pageRootCentered: { justifyContent: "center" },

  pageHeader: {
    width: "100%",
    backgroundColor: BRAND.primary,
    backgroundImage: `url(${TEXTURE_URL})`,
    backgroundSize: "cover", backgroundPosition: "center",
    padding: "1.25rem 2rem 2rem",
    boxSizing: "border-box",
    display: "flex", flexDirection: "column", alignItems: "center",
    [MEDIA.smDown]: { padding: "1rem 1rem 1.5rem" },
  },
  pageHeaderInner: {
    width: "100%", maxWidth: "1440px", boxSizing: "border-box",
  },
  pageInner: {
    width: "100%", maxWidth: "1440px", boxSizing: "border-box",
    padding: "0 2rem 2.5rem",
    [MEDIA.smDown]: { padding: "0 1rem 1.5rem" },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TYPOGRAPHY
  // ═══════════════════════════════════════════════════════════════════════════

  // Page titles (used in page headers)
  pageTitle: {
    ...FONT.h1, color: BRAND.textMain, fontFamily: FONT.family, marginBottom: "0.25rem",
  },
  pageTitleWhite: {
    ...FONT.h1, color: BRAND.white, fontFamily: FONT.family, marginBottom: "0.2rem",
  },
  pageSubtitle: {
    ...FONT.h3, color: BRAND.textMuted, fontFamily: FONT.family, fontWeight: 400, marginBottom: "2rem",
  },
  pageSubtitleWhite: {
    ...FONT.h3, color: "rgba(255,255,255,0.8)", fontFamily: FONT.family, fontWeight: 500,
  },

  // Section label (UPPERCASE muted)
  sectionLabel: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.75rem",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CARDS & CONTAINERS
  // ═══════════════════════════════════════════════════════════════════════════

  card: {
    borderRadius: RADIUS.lg, padding: "1.5rem",
    backgroundColor: BRAND.white, boxShadow: SHADOW.soft,
    border: `1.5px solid ${BRAND.border}`,
    fontFamily: FONT.family, boxSizing: "border-box",
    [MEDIA.smDown]: { padding: "1.1rem", borderRadius: RADIUS.md },
  },
  cardClickable: {
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    "&:hover": { transform: "translateY(-2px)", boxShadow: SHADOW.medium, borderColor: BRAND.primary },
  },
  cardPrimary: {
    backgroundColor: BRAND.primary, color: BRAND.white, border: "none",
    boxShadow: "0 4px 16px rgba(0,58,34,0.2)",
  },
  cardSelected: {
    borderColor: BRAND.primary, borderWidth: "2px",
    boxShadow: `0 0 0 3px ${BRAND.tertiary}`,
  },

  // Section card (white card with header bar)
  sectionCard: {
    backgroundColor: BRAND.white, borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.soft,
    overflow: "hidden", marginBottom: "1.25rem",
  },
  sectionHead: {
    display: "flex", alignItems: "center", gap: "0.6rem",
    padding: "0.85rem 1.25rem",
    borderBottom: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.background,
    [MEDIA.smDown]: { padding: "0.75rem 1rem", flexWrap: "wrap" },
  },
  sectionTitle: {
    ...FONT.meta, textTransform: "uppercase", letterSpacing: "0.08em",
    color: BRAND.textMain, fontFamily: FONT.family,
  },
  sectionCount: {
    ...FONT.tiny, backgroundColor: BRAND.border, color: BRAND.textMuted,
    borderRadius: "20px", padding: "1px 7px", fontFamily: FONT.family,
  },
  sectionLink: {
    ...FONT.meta, color: BRAND.primary, fontFamily: FONT.family,
    cursor: "pointer", display: "flex", alignItems: "center", gap: "0.2rem",
    "&:hover": { textDecoration: "underline" },
  },
  emptySection: {
    padding: "2rem 1.25rem", textAlign: "center",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LIST ROWS (used in tables, recent items, dossier lists)
  // ═══════════════════════════════════════════════════════════════════════════

  listRow: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.75rem 1.25rem",
    borderBottom: `1px solid ${BRAND.border}`,
    cursor: "pointer", transition: "background 0.12s",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
    [MEDIA.smDown]: { padding: "0.7rem 1rem", gap: "0.6rem" },
  },
  listIcon: {
    width: "32px", height: "32px", borderRadius: RADIUS.md,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  listInfo: {
    flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.1rem",
  },
  listName: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  listMeta: {
    ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family,
    display: "flex", alignItems: "center", gap: "0.3rem",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STATUS CHIPS, TAGS, PILLS
  // ═══════════════════════════════════════════════════════════════════════════

  // Status chip (dossier/doc status)
  statusChip: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 10px", borderRadius: "20px",
    fontSize: "0.74rem", fontWeight: 500, lineHeight: 1.3, fontFamily: FONT.family,
  },

  // Indicator tag (colored bg + dot + label + value)
  indicatorTag: {
    display: "inline-flex", alignItems: "center", gap: "0.35rem",
    borderRadius: RADIUS.sm, padding: "3px 8px",
    ...FONT.small, fontFamily: FONT.family, whiteSpace: "nowrap", cursor: "default",
    fontSize: "0.78rem",
  },
  indicatorDot: {
    width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0,
  },
  indicatorLabel: {
    color: BRAND.textMuted, fontWeight: 500,
  },
  indicatorValue: {
    fontWeight: 600,
  },

  // Attribute tag (neutral bg)
  attrTag: {
    display: "inline-flex", alignItems: "center", gap: "0.3rem",
    borderRadius: RADIUS.sm, padding: "3px 8px",
    backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}`,
    ...FONT.small, fontFamily: FONT.family, whiteSpace: "nowrap",
    maxWidth: "260px", overflow: "hidden", cursor: "default",
    fontSize: "0.78rem",
  },
  attrTagLabel: {
    color: BRAND.textMuted, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis",
  },
  attrTagValue: {
    color: BRAND.textMain, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis",
  },

  // Identifier pill (copyable)
  identifierPill: {
    display: "inline-flex", alignItems: "center", gap: "0.3rem",
    backgroundColor: BRAND.background, borderRadius: RADIUS.md,
    padding: "3px 8px", ...FONT.small, fontWeight: 500,
    color: BRAND.textMuted, fontFamily: FONT.family, flexShrink: 0,
    cursor: "pointer", transition: "border-color 0.15s",
    border: "1px solid transparent",
    "&:hover": { borderColor: BRAND.primary },
    "& .copyIcon": { opacity: 0, fontSize: "10px", transition: "opacity 0.15s", marginLeft: "0.15rem" },
    "&:hover .copyIcon": { opacity: 0.6 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ICON WRAPS
  // ═══════════════════════════════════════════════════════════════════════════

  iconWrap: {
    width: "44px", height: "44px", borderRadius: RADIUS.md,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  iconWrapSm: {
    width: "36px", height: "36px", borderRadius: RADIUS.md,
  },
  iconWrapXs: {
    width: "32px", height: "32px", borderRadius: "8px",
  },
  iconWrapPrimary:   { backgroundColor: BRAND.secondary },
  iconWrapSecondary: { backgroundColor: BRAND.background },
  iconWrapMuted:     { backgroundColor: BRAND.border },

  // ═══════════════════════════════════════════════════════════════════════════
  // ROW OF CARDS
  // ═══════════════════════════════════════════════════════════════════════════

  cardsRow: {
    display: "flex", gap: "1.25rem", flexWrap: "wrap",
    width: "100%", boxSizing: "border-box",
    [MEDIA.smDown]: { gap: "0.85rem" },
  },
  cardsRowCentered: { justifyContent: "center" },

  // ═══════════════════════════════════════════════════════════════════════════
  // BUTTONS
  // ═══════════════════════════════════════════════════════════════════════════

  btnPrimary: {
    backgroundColor: BRAND.primary, color: BRAND.white,
    border: "none", borderRadius: RADIUS.md,
    padding: "0.65rem 1.5rem", fontFamily: FONT.family,
    ...FONT.h3, fontWeight: 600, cursor: "pointer",
    transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  btnSecondary: {
    backgroundColor: BRAND.white, color: BRAND.textMain,
    border: `1.5px solid ${BRAND.border}`, borderRadius: RADIUS.md,
    padding: "0.65rem 1.5rem", fontFamily: FONT.family,
    ...FONT.h3, fontWeight: 500, cursor: "pointer",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: BRAND.primary },
  },
  btnSmall: {
    padding: "0.4rem 0.9rem", fontSize: "0.78rem",
  },
  btnDanger: {
    "&:hover": { borderColor: "#EF4444 !important", color: "#EF4444 !important" },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FORM FIELDS
  // ═══════════════════════════════════════════════════════════════════════════

  fieldLabel: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    marginBottom: "0.35rem", fontSize: "0.7rem",
  },
  fieldInput: {
    height: "36px", padding: "0 0.75rem", borderRadius: "8px",
    border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family,
    ...FONT.body, color: BRAND.textMain, backgroundColor: BRAND.white,
    outline: "none", boxSizing: "border-box", width: "100%",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
  },
  fieldSelect: {
    height: "36px", padding: "0 0.75rem", borderRadius: "8px",
    border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family,
    ...FONT.body, color: BRAND.textMain, backgroundColor: BRAND.white,
    outline: "none", width: "100%", cursor: "pointer",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HERO (used in detail pages)
  // ═══════════════════════════════════════════════════════════════════════════

  heroCard: {
    borderRadius: RADIUS.lg, overflow: "hidden",
    border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.medium,
    marginBottom: "1.25rem",
  },
  heroTop: {
    backgroundColor: BRAND.primary,
    backgroundImage: `url(${TEXTURE_URL})`,
    backgroundSize: "cover", backgroundPosition: "center",
    padding: "1.5rem 2rem",
    [MEDIA.smDown]: { padding: "1.1rem 1.1rem" },
  },
  heroTitle: {
    ...FONT.h1, color: BRAND.white, fontFamily: FONT.family,
  },
  heroMeta: {
    ...FONT.small, color: "rgba(255,255,255,0.85)", fontWeight: 500,
    fontFamily: FONT.family, marginTop: "0.3rem",
  },
  heroBody: {
    backgroundColor: BRAND.white, padding: "1.5rem 2rem",
    display: "flex", flexDirection: "column", gap: "0.75rem",
    [MEDIA.smDown]: { padding: "1.1rem 1.1rem" },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BACK BUTTON
  // ═══════════════════════════════════════════════════════════════════════════

  backBtn: {
    display: "flex", alignItems: "center", gap: "0.35rem",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family,
    cursor: "pointer", marginBottom: "1.75rem", width: "fit-content",
    transition: "color 0.15s",
    "&:hover": { color: BRAND.primary },
  },
  backBtnWhite: {
    display: "flex", alignItems: "center", gap: "0.35rem",
    ...FONT.body, color: "rgba(255,255,255,0.7)", fontFamily: FONT.family,
    cursor: "pointer", marginBottom: "0.75rem", width: "fit-content",
    transition: "color 0.15s",
    "&:hover": { color: BRAND.white },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGINATION
  // ═══════════════════════════════════════════════════════════════════════════

  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0.85rem 1.25rem", borderTop: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.background,
    [MEDIA.smDown]: {
      flexDirection: "column", gap: "0.6rem",
      padding: "0.75rem 1rem", alignItems: "stretch",
    },
  },
  paginationInfo: {
    ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
    [MEDIA.smDown]: { textAlign: "center" },
  },
  paginationControls: {
    display: "flex", alignItems: "center", gap: "0.35rem",
    [MEDIA.smDown]: { justifyContent: "center" },
  },
  pageBtn: {
    height: "32px", minWidth: "32px", padding: "0 0.5rem",
    borderRadius: "8px", border: `1.5px solid ${BRAND.border}`,
    backgroundColor: BRAND.white, color: BRAND.textMain,
    fontFamily: FONT.family, ...FONT.body, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "border-color 0.15s, color 0.15s",
    "&:hover:not(:disabled)": { borderColor: BRAND.primary, color: BRAND.primary },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  pageBtnActive: {
    backgroundColor: BRAND.primary, borderColor: BRAND.primary,
    color: `${BRAND.white} !important`, fontWeight: 700,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TOGGLE PILLS (e.g. Actifs / Archivés)
  // ═══════════════════════════════════════════════════════════════════════════

  toggleGroup: {
    display: "inline-flex", borderRadius: RADIUS.md,
    border: `1.5px solid ${BRAND.border}`, overflow: "hidden",
    backgroundColor: BRAND.white,
  },
  toggleBtn: {
    padding: "0.45rem 1rem", border: "none", cursor: "pointer",
    ...FONT.body, fontWeight: 600, fontFamily: FONT.family,
    backgroundColor: "transparent", color: BRAND.textMuted,
    transition: "background 0.15s, color 0.15s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  toggleBtnActive: {
    backgroundColor: BRAND.primary, color: BRAND.white,
    "&:hover": { backgroundColor: BRAND.primary },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FILTER BAR
  // ═══════════════════════════════════════════════════════════════════════════

  filterCard: {
    width: "100%", backgroundColor: BRAND.white, borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.soft,
    padding: "1.25rem 1.5rem", marginBottom: "1.25rem", boxSizing: "border-box",
    [MEDIA.smDown]: { padding: "0.9rem 1rem", borderRadius: RADIUS.md },
  },
  filterRow: {
    display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end",
  },
  filterField: {
    display: "flex", flexDirection: "column", gap: "0.35rem",
    flex: "1 1 200px", minWidth: 0,
    [MEDIA.smDown]: { flex: "1 1 100%" },
  },
  filterActions: {
    display: "flex", gap: "0.5rem", alignItems: "flex-end", flexShrink: 0,
    [MEDIA.smDown]: { width: "100%", "& > button": { flex: 1 } },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DIALOG
  // ═══════════════════════════════════════════════════════════════════════════

  dialogPaper: {
    borderRadius: RADIUS.lg, fontFamily: FONT.family,
    maxHeight: "85vh",
    [MEDIA.smDown]: {
      borderRadius: RADIUS.md,
      margin: "0.5rem",
      width: "calc(100% - 1rem)",
      maxHeight: "calc(100vh - 1rem)",
    },
  },
  dialogTitle: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    fontFamily: FONT.family, ...FONT.h2,
    color: BRAND.white, backgroundColor: BRAND.primary,
    paddingBottom: "12px", flexShrink: 0,
  },
}));
